/**
 * Which parts of a rundown are on air or next right now, for the editor's
 * "protect on-air parts" safety (`lib/editor/onAirGuard.ts`): the active
 * productions running the rundown — itself, or the playlist it sits in —
 * picked from the active productions (`activeProductionsQuery`: one small
 * listing, live over the change socket, D35), and
 * each one's playout state (`GET /productions/{id}/playout`), polled while
 * there is one. The data-hook pattern (`rundowns.ts`).
 *
 * **Polled, not streamed.** The editor needs only the two part ids, and a
 * playout stream per production (`api/playout.ts`) is the operator screen's
 * cost; a 2 s poll keeps the protection at most one poll behind a take, and
 * a rundown nothing runs costs no requests at all. A
 * production whose state cannot be read (its process unreachable, the cloud
 * without a link) contributes nothing: the protection is a safety net, never
 * a reason the editor fails to open.
 */
import { allPages, type ApiClient } from '@acp/api-client/client.js';
import type { PlayoutStateWire } from '@acp/api-contract/playout.js';
import type { ProductionSummaryWire } from '@acp/api-contract/productions.js';
import { queryOptions } from '@tanstack/react-query';
import type { OnAirParts } from '../lib/editor/onAirGuard.js';
import { productionKeys } from './productions.js';

/** How often the on-air parts are re-read while the editor is open. */
export const ON_AIR_POLL_MS = 2_000;

/** The active productions running `rundownId`, alone or through its playlist. */
export function productionsRunning(productions: readonly ProductionSummaryWire[], rundownId: string, playlistId: string | null): ProductionSummaryWire[] {
  return productions.filter((p) => p.status === 'active' && (p.rundownId === rundownId || (playlistId !== null && p.playlistId === playlistId)));
}

/** The on-air and next parts of `rundownId` across the productions' states. */
export function onAirPartsOf(states: readonly PlayoutStateWire[], rundownId: string): OnAirParts {
  const onAir = new Set<string>();
  const next = new Set<string>();
  for (const state of states) {
    if (state.current !== null && state.current.instance.rundownId === rundownId) onAir.add(state.current.instance.partId);
    if (state.next !== null && state.next.instance.rundownId === rundownId) next.add(state.next.instance.partId);
  }
  return { onAir, next };
}

/** The tenant's active productions (the server filters: the history of ended ones is never paged through). */
export function activeProductionsQuery(api: ApiClient) {
  return queryOptions({
    // Under the productions LIST key: a production created, activated or ended invalidates it (`changes.ts`' keysForChange).
    queryKey: [...productionKeys.list(), 'active'] as const,
    queryFn: async (): Promise<ProductionSummaryWire[]> =>
      allPages(async (pageToken) => {
        const page = await api.call('listProductions', { params: null, query: pageToken === undefined ? { status: 'active' } : { status: 'active', pageToken }, body: null });
        return { items: page.productions, nextPageToken: page.nextPageToken };
      }),
  });
}

async function readState(api: ApiClient, productionId: string): Promise<PlayoutStateWire | null> {
  try {
    return (await api.call('getPlayout', { params: { productionId }, query: null, body: null })).state;
  } catch {
    return null;
  }
}

/** The on-air parts of `rundownId` in the productions `productionIds` (`productionsRunning`); idle while there are none. */
export function onAirPartsQuery(api: ApiClient, rundownId: string, productionIds: readonly string[]) {
  return queryOptions({
    // Under `productions`: a production created, activated or ended (the change socket) re-reads it at once.
    queryKey: [...productionKeys.all, 'on-air', rundownId, ...productionIds] as const,
    queryFn: async (): Promise<OnAirParts> => {
      const states = await Promise.all(productionIds.map((id) => readState(api, id)));
      return onAirPartsOf(
        states.filter((s) => s !== null),
        rundownId,
      );
    },
    enabled: productionIds.length > 0,
    refetchInterval: ON_AIR_POLL_MS,
    retry: false,
  });
}
